import { useEffect, useState } from 'react'
import { shallowEqual } from 'react-redux'
import { useDispatch, useSelector } from '../../../../Flux/index.js'
import type { AppDispatch } from '../../../../Flux/index.js'
import type { State } from '../../../../Flux/Slice/index.js'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import type { ScopeLoaded } from '../../../../Flux/Slice/Visualization/ScopeLoaded.js'
import { selectScopeLoaded } from '../../../../Flux/Slice/Visualization/ScopeLoaded.js'
import type { VizScope } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'

const NOT_LOADED: ScopeLoaded = { scope: false, samples: false, orders: false }

/**
 * Points the workspace at a scope, requests its inputs, and reports which of them have arrived for it.
 * `request` must be stable (module level).
 *
 * Nothing counts before this hook's own requests have gone out: on the render that enters a scope, the
 * store still holds whatever the previous page loaded, which may well be this scope's — and would hydrate
 * the workspace in the same commit that sends the requests resetting it.
 */
export function useScopeLoad(kind: VizScope['kind'], id: string | undefined, request: (dispatch: AppDispatch, id: string) => void): ScopeLoaded {
  const dispatch = useDispatch()
  const [requestedFor, setRequestedFor] = useState<string>()

  useEffect(() => {
    if (!id) return
    dispatch(visualizationSlice.actions.reset())
    dispatch(visualizationSlice.actions.setScope({ kind, id }))
    request(dispatch, id)
    setRequestedFor(id)
  }, [kind, id, request, dispatch])

  const loaded = useSelector((s: typeof State) => selectScopeLoaded(s, id ? { kind, id } : null), shallowEqual)
  return !!id && requestedFor === id ? loaded : NOT_LOADED
}
